import { useCallback, useEffect } from 'react';
import { isYouTubeLink, getYouTubeId } from '../utils/whiteboardUtils';

export function useWhiteboardElements({
  setShapes, setPdfModal,
  panRef, updatePan, shapes,
  onInsertText, onBroadcastPatch,
}) {
  const createExerciseElement = useCallback((exerciseData, exerciseType, x, y) => {
    const scale = panRef.current.scale || 1;
    const centerX = (-panRef.current.x + window.innerWidth / 2) / scale;
    const centerY = (-panRef.current.y + window.innerHeight / 2) / scale;
    const el = {
      type: 'exercise',
      x: (x !== undefined && x !== null && x !== 0) ? x : centerX - 200,
      y: (y !== undefined && y !== null && y !== 0) ? y : centerY - 100,
      exerciseType,
      exerciseData,
      width: exerciseType === 'text' ? 520 : 400,
      height: exerciseType === 'text' ? 260 : 200,
      id: Date.now() + Math.random(),
    };
    setShapes(prev => [...prev, el]);
    return el;
  }, [setShapes]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMaterialElement = useCallback((materialData, x, y, setPdfModalFromParent) => {
    let el;
    const scale = panRef.current.scale || 1;
    const cx = (x !== undefined && x !== null) ? x : (-panRef.current.x + window.innerWidth / 2) / scale - 200;
    const cy = (y !== undefined && y !== null) ? y : (-panRef.current.y + window.innerHeight / 2) / scale - 150;

    if (materialData.type === 'link') {
      if (isYouTubeLink(materialData.value)) {
        const videoId = getYouTubeId(materialData.value);
        if (videoId) {
          el = {
            type: 'youtube',
            x: cx, y: cy,
            videoId,
            width: 400, height: 300,
            id: Date.now() + Math.random(),
            isPlaying: false,
            clickCount: 0,
          };
        } else {
          el = {
            type: 'link',
            x: cx, y: cy,
            src: materialData.value,
            name: materialData.name,
            width: 400, height: 100,
            id: Date.now() + Math.random(),
            materialData,
          };
        }
      } else {
        el = {
          type: 'link',
          x: cx, y: cy,
          src: materialData.value,
          name: materialData.name,
          width: 400, height: 100,
          id: Date.now() + Math.random(),
          materialData,
        };
      }
    } else if (materialData.type === 'file') {
      const extension = materialData.extension || materialData.name.split('.').pop().toLowerCase();

      if (extension === 'pdf') {
        const handler = setPdfModalFromParent || setPdfModal;
        handler({
          pdfUrl: materialData.value,
          filename: materialData.name,
          x: cx,
          y: cy,
        });
        return null;
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        el = { type: 'image', x: cx, y: cy, src: materialData.value, width: 400, height: 300, id: Date.now() + Math.random(), filename: materialData.name };
      } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        el = { type: 'video', x: cx, y: cy, src: materialData.value, width: 400, height: 300, id: Date.now() + Math.random(), filename: materialData.name };
      } else if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
        el = { type: 'audio', x: cx, y: cy, src: materialData.value, width: 400, height: 100, id: Date.now() + Math.random(), filename: materialData.name };
      } else {
        el = { type: 'file', x: cx, y: cy, src: materialData.value, name: materialData.name, width: 400, height: 300, id: Date.now() + Math.random(), materialData };
      }
    }

    if (el) {
      setShapes(prev => [...prev, el]);
    }
    return el;
  }, [setShapes, setPdfModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const createGridElement = useCallback((rows, cols) => {
    const scale = panRef.current.scale || 1;
    const centerX = (-panRef.current.x + window.innerWidth / 2) / scale;
    const centerY = (-panRef.current.y + window.innerHeight / 2) / scale;
    const cellW = 120;
    const cellH = 44;
    const el = {
      type: 'grid',
      x: centerX - (cols * cellW) / 2,
      y: centerY - (rows * cellH) / 2,
      rows,
      cols,
      colWidths: Array(cols).fill(cellW),
      rowHeights: Array(rows).fill(cellH),
      width: cols * cellW,
      height: rows * cellH,
      headerRow: true,
      cells: Array.from({ length: rows }, () => Array(cols).fill('')),
      id: Date.now() + Math.random(),
    };
    setShapes(prev => [...prev, el]);
    return el;
  }, [setShapes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onInsertText) {
      onInsertText(null, createExerciseElement, createMaterialElement, setPdfModal, createGridElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createExerciseElement, createMaterialElement, createGridElement]);

  const centerOnExercise = useCallback((exerciseShape) => {
    const expandedWidth = 900;
    const expandedHeight = 700;

    setShapes(prev => prev.map(s =>
      s.id === exerciseShape.id ? { ...s, width: expandedWidth, height: expandedHeight } : s
    ));

    const exerciseCenterX = exerciseShape.x + expandedWidth / 2;
    const exerciseCenterY = exerciseShape.y + expandedHeight / 2;
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    const scaleX = window.innerWidth / (expandedWidth + 100);
    const scaleY = window.innerHeight / (expandedHeight + 100);
    const newScale = Math.min(scaleX, scaleY, 1);
    const newX = viewportCenterX - exerciseCenterX * newScale;
    const newY = viewportCenterY - exerciseCenterY * newScale;

    const startPan = panRef.current;
    const startTime = Date.now();
    const duration = 500;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      updatePan({
        x: startPan.x + (newX - startPan.x) * ease,
        y: startPan.y + (newY - startPan.y) * ease,
        scale: startPan.scale + (newScale - startPan.scale) * ease,
      });
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    if (onBroadcastPatch) {
      try {
        const lightShapes = Array.isArray(shapes)
          ? shapes.map(s => (s && s.type === 'image' && typeof s.src === 'string' && s.src.startsWith('data:'))
              ? { ...s, src: '[LARGE_IMAGE_DATA]' } : s)
          : [];
        onBroadcastPatch({
          type: 'expand',
          shapeId: exerciseShape.id,
          width: expandedWidth,
          height: expandedHeight,
          pan: { x: newX, y: newY, scale: newScale },
          shapes: lightShapes,
          at: Date.now(),
        });
      } catch (_) {}
    }
  }, [setShapes, panRef, updatePan, shapes, onBroadcastPatch]);

  return { createExerciseElement, createMaterialElement, centerOnExercise, createGridElement };
}
